(() => {
const initShunyangHome = (root) => {
  if (!root || root.dataset.shunyangInitialized === 'true') return;
  root.dataset.shunyangInitialized = 'true';

  const menuButton = root.querySelector('.menu-button');
  const mobileNav = root.querySelector('.mobile-nav');
  const quoteModal = root.querySelector('.quote-modal');
  const modalBackdrop = root.querySelector('.modal-backdrop');
  const toast = root.querySelector('.toast');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let toastTimer;
  let lastFocusedElement;

  const setupMotion = () => {
    const hero = root.querySelector('.hero');
    const heroVisual = root.querySelector('.hero__visual');
    const siteHeader = root.querySelector('.site-header');
    const revealGroups = [
      ['.section-heading', 'rise', 0],
      ['.solution-card', 'rise', 90],
      ['.product-card', 'rise', 75],
      ['.capability-carousel__shell', 'scale', 0],
      ['.project-band__intro', 'left', 0],
      ['.process-list li', 'right', 85],
      ['.marquee-heading', 'rise', 0],
      ['.video-deck__card', 'rise', 70],
      ['.cooperation-heading', 'rise', 0],
      ['.cooperation-card', 'rise', 90],
      ['.cooperation-detail', 'rise', 0],
      ['.cta-section__inner', 'scale', 0],
      ['.footer__grid > *', 'rise', 65]
    ];
    const revealItems = [];

    revealGroups.forEach(([selector, motion, stagger]) => {
      root.querySelectorAll(selector).forEach((item, index) => {
        item.dataset.motion = motion;
        item.style.setProperty('--motion-delay', `${Math.min(index * stagger, 360)}ms`);
        revealItems.push(item);
      });
    });

    root.classList.add('is-motion-ready');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-in-view'));
      root.classList.add('is-motion-started');
    } else {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in-view');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
      revealItems.forEach((item) => revealObserver.observe(item));
      requestAnimationFrame(() => root.classList.add('is-motion-started'));
    }

    const animateNumber = (element) => {
      if (element.dataset.counted === 'true') return;
      element.dataset.counted = 'true';
      const finalText = element.dataset.finalValue || element.textContent.trim();
      const numberMatch = finalText.match(/[\d,.]+/);
      if (!numberMatch) return;
      const target = Number(numberMatch[0].replace(/,/g, ''));
      const prefix = finalText.slice(0, numberMatch.index);
      const suffix = finalText.slice(numberMatch.index + numberMatch[0].length);
      const useGrouping = numberMatch[0].includes(',');
      const startedAt = performance.now();
      const duration = 1250;
      element.setAttribute('aria-label', finalText);

      const tick = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        const value = Math.round(target * eased);
        element.textContent = `${prefix}${useGrouping ? value.toLocaleString('en-US') : value}${suffix}`;
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const numberItems = [...root.querySelectorAll('.hero__trust strong')];
    numberItems.forEach((item, index) => {
      item.dataset.finalValue = item.textContent.trim();
      item.dataset.countDelay = '0';
    });
    if (reduceMotion || !('IntersectionObserver' in window)) {
      numberItems.forEach((item) => { item.textContent = item.dataset.finalValue; });
    } else {
      const prepareNumber = (element) => {
        const finalText = element.dataset.finalValue || element.textContent.trim();
        const numberMatch = finalText.match(/[\d,.]+/);
        if (!numberMatch) return;
        const prefix = finalText.slice(0, numberMatch.index);
        const suffix = finalText.slice(numberMatch.index + numberMatch[0].length);
        element.setAttribute('aria-label', finalText);
        element.textContent = `${prefix}0${suffix}`;
      };
      const numberObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          prepareNumber(entry.target);
          window.setTimeout(() => animateNumber(entry.target), Number(entry.target.dataset.countDelay || 0));
          numberObserver.unobserve(entry.target);
        });
      }, { threshold: 0.6 });
      numberItems.forEach((item) => numberObserver.observe(item));
    }

    const processItems = [...root.querySelectorAll('.process-list li')];
    if (processItems.length) {
      processItems[0].classList.add('is-active');
      if (!reduceMotion && 'IntersectionObserver' in window) {
        const processObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            processItems.forEach((item) => item.classList.toggle('is-active', item === entry.target));
          });
        }, { threshold: 0.45, rootMargin: '-28% 0px -42% 0px' });
        processItems.forEach((item) => processObserver.observe(item));
      }
    }

    if (!reduceMotion && hero && heroVisual) {
      const setHeroDepth = (event) => {
        const bounds = hero.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 20;
        const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 16;
        heroVisual.style.setProperty('--hero-main-x', `${x * 0.3}px`);
        heroVisual.style.setProperty('--hero-main-y', `${y * 0.3}px`);
        heroVisual.style.setProperty('--hero-top-x', `${x}px`);
        heroVisual.style.setProperty('--hero-top-y', `${y}px`);
        heroVisual.style.setProperty('--hero-bottom-x', `${x * -0.7}px`);
        heroVisual.style.setProperty('--hero-bottom-y', `${y * -0.7}px`);
        hero.style.setProperty('--pointer-x', `${((event.clientX - bounds.left) / bounds.width) * 100}%`);
        hero.style.setProperty('--pointer-y', `${((event.clientY - bounds.top) / bounds.height) * 100}%`);
      };
      const resetHeroDepth = () => {
        ['--hero-main-x', '--hero-main-y', '--hero-top-x', '--hero-top-y', '--hero-bottom-x', '--hero-bottom-y'].forEach((property) => heroVisual.style.setProperty(property, '0px'));
      };
      hero.addEventListener('pointermove', setHeroDepth);
      hero.addEventListener('pointerleave', resetHeroDepth);
    }

    let scrollFrame;
    const updateScrollMotion = () => {
      scrollFrame = null;
      siteHeader?.classList.toggle('is-scrolled', window.scrollY > 28);
      revealItems.forEach((item) => {
        if (item.classList.contains('is-in-view')) return;
        const bounds = item.getBoundingClientRect();
        if (bounds.top < window.innerHeight * 0.95 && bounds.bottom > 0) item.classList.add('is-in-view');
      });
      if (heroVisual && !reduceMotion && window.innerWidth > 800) {
        const heroBounds = hero?.getBoundingClientRect();
        const progress = heroBounds ? Math.min(Math.max(-heroBounds.top / Math.max(heroBounds.height, 1), 0), 1) : 0;
        heroVisual.style.setProperty('--hero-scroll', `${progress * 34}px`);
      } else {
        heroVisual?.style.setProperty('--hero-scroll', '0px');
      }
    };
    const requestScrollMotion = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(updateScrollMotion);
    };
    updateScrollMotion();
    window.addEventListener('scroll', requestScrollMotion, { passive: true });
    window.addEventListener('resize', requestScrollMotion, { passive: true });

    const cooperationShell = root.querySelector('.cooperation-shell');
    if (cooperationShell && !reduceMotion) {
      cooperationShell.addEventListener('pointermove', (event) => {
        const bounds = cooperationShell.getBoundingClientRect();
        cooperationShell.style.setProperty('--spot-x', `${((event.clientX - bounds.left) / bounds.width) * 100}%`);
        cooperationShell.style.setProperty('--spot-y', `${((event.clientY - bounds.top) / bounds.height) * 100}%`);
      });
      cooperationShell.addEventListener('pointerleave', () => {
        cooperationShell.style.setProperty('--spot-x', '50%');
        cooperationShell.style.setProperty('--spot-y', '20%');
      });
    }
  };

  setupMotion();

  const showToast = (message) => {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const closeMenu = () => {
    mobileNav?.classList.remove('is-open');
    menuButton?.setAttribute('aria-expanded', 'false');
  };
  menuButton?.addEventListener('click', () => {
    const open = mobileNav.classList.toggle('is-open');
    menuButton.setAttribute('aria-expanded', String(open));
  });
  mobileNav?.querySelectorAll('a, button').forEach((item) => item.addEventListener('click', closeMenu));

  const openQuote = (product) => {
    if (!quoteModal || !modalBackdrop) return;
    lastFocusedElement = document.activeElement;
    const interest = quoteModal.querySelector('[name="contact[Product interest]"]');
    if (interest && product) interest.value = product;
    quoteModal.classList.add('is-open');
    modalBackdrop.classList.add('is-open');
    quoteModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    window.setTimeout(() => quoteModal.querySelector('.modal-close')?.focus(), 50);
  };
  const closeQuote = () => {
    quoteModal?.classList.remove('is-open');
    modalBackdrop?.classList.remove('is-open');
    quoteModal?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    lastFocusedElement?.focus?.();
  };
  root.querySelectorAll('.quote-trigger').forEach((button) => button.addEventListener('click', () => openQuote(button.dataset.product)));
  root.querySelectorAll('[data-close-modal]').forEach((button) => button.addEventListener('click', closeQuote));

  const productCards = [...root.querySelectorAll('.product-card')];
  productCards.forEach((card) => {
    const cardUrl = card.dataset.cardUrl;
    if (!cardUrl) return;

    card.addEventListener('click', (event) => {
      const separateControl = event.target.closest('button, form, input, select, textarea, .quick-view, h3 a');
      if (separateControl) return;
      event.preventDefault();
      window.location.assign(cardUrl);
    });
  });
  const productGridToggle = root.querySelector('[data-product-grid-toggle]');
  const productGridToggleWrap = root.querySelector('[data-product-grid-toggle-wrap]');
  const productGridToggleLabel = root.querySelector('[data-product-grid-toggle-label]');
  let activeProductFilter = 'all';
  let isProductGridExpanded = false;

  const updateProductGrid = () => {
    const matchingCards = productCards.filter((card) => activeProductFilter === 'all'
      ? card.dataset.showInAll !== 'false'
      : card.dataset.category.includes(activeProductFilter));

    productCards.forEach((card) => {
      const matchingIndex = matchingCards.indexOf(card);
      const matchesFilter = matchingIndex !== -1;
      card.classList.toggle('is-hidden', !matchesFilter);
      card.classList.toggle('is-hidden-by-limit', matchesFilter && !isProductGridExpanded && matchingIndex >= 8);
    });

    const hiddenCount = Math.max(matchingCards.length - 8, 0);
    if (productGridToggleWrap) productGridToggleWrap.hidden = hiddenCount === 0;
    if (productGridToggle) productGridToggle.setAttribute('aria-expanded', String(isProductGridExpanded));
    if (productGridToggleLabel) productGridToggleLabel.textContent = isProductGridExpanded
      ? 'Show fewer products'
      : `View ${hiddenCount} more product${hiddenCount === 1 ? '' : 's'}`;
  };

  root.querySelectorAll('.filter-pill').forEach((button) => {
    button.addEventListener('click', () => {
      root.querySelectorAll('.filter-pill').forEach((pill) => pill.classList.remove('is-active'));
      button.classList.add('is-active');
      activeProductFilter = button.dataset.filter;
      isProductGridExpanded = false;
      updateProductGrid();
    });
  });

  productGridToggle?.addEventListener('click', () => {
    isProductGridExpanded = !isProductGridExpanded;
    updateProductGrid();
  });
  updateProductGrid();

  const capabilityCarousel = root.querySelector('[data-capability-carousel]');
  if (capabilityCarousel) {
    const capabilitySlides = [...capabilityCarousel.querySelectorAll('[data-capability-slide]')];
    const capabilityDots = [...capabilityCarousel.querySelectorAll('[data-capability-dot]')];
    const capabilityStatus = capabilityCarousel.querySelector('[data-capability-status]');
    const capabilityPrevious = capabilityCarousel.querySelector('[data-capability-prev]');
    const capabilityNext = capabilityCarousel.querySelector('[data-capability-next]');
    let capabilityIndex = 0;
    let capabilityTimer;
    let capabilityPointerPaused = false;
    let capabilityFocusPaused = false;

    const renderCapabilityCarousel = () => {
      capabilitySlides.forEach((slide, index) => {
        let offset = (index - capabilityIndex + capabilitySlides.length) % capabilitySlides.length;
        if (offset > capabilitySlides.length / 2) offset -= capabilitySlides.length;
        const current = offset === 0;
        slide.dataset.carouselPosition = String(offset);
        slide.classList.toggle('is-current', current);
        slide.setAttribute('aria-current', String(current));
      });
      capabilityDots.forEach((dot, index) => {
        const current = index === capabilityIndex;
        dot.classList.toggle('is-active', current);
        dot.setAttribute('aria-current', String(current));
      });
      if (capabilityStatus) capabilityStatus.textContent = `${String(capabilityIndex + 1).padStart(2, '0')} / ${String(capabilitySlides.length).padStart(2, '0')}`;
    };

    const showCapabilitySlide = (index) => {
      capabilityIndex = (index + capabilitySlides.length) % capabilitySlides.length;
      renderCapabilityCarousel();
    };
    const stopCapabilityCarousel = () => {
      window.clearInterval(capabilityTimer);
      capabilityTimer = undefined;
    };
    const startCapabilityCarousel = () => {
      stopCapabilityCarousel();
      if (reduceMotion || capabilityPointerPaused || capabilityFocusPaused || document.hidden || capabilitySlides.length < 2) return;
      capabilityTimer = window.setInterval(() => showCapabilitySlide(capabilityIndex + 1), 4800);
    };

    capabilityPrevious?.addEventListener('click', () => {
      showCapabilitySlide(capabilityIndex - 1);
      startCapabilityCarousel();
    });
    capabilityNext?.addEventListener('click', () => {
      showCapabilitySlide(capabilityIndex + 1);
      startCapabilityCarousel();
    });
    capabilitySlides.forEach((slide, index) => slide.addEventListener('click', () => {
      showCapabilitySlide(index);
      startCapabilityCarousel();
    }));
    capabilityDots.forEach((dot, index) => dot.addEventListener('click', () => {
      showCapabilitySlide(index);
      startCapabilityCarousel();
    }));
    capabilityCarousel.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      showCapabilitySlide(capabilityIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    capabilityCarousel.addEventListener('pointerenter', () => {
      capabilityPointerPaused = true;
      stopCapabilityCarousel();
    });
    capabilityCarousel.addEventListener('pointerleave', () => {
      capabilityPointerPaused = false;
      startCapabilityCarousel();
    });
    capabilityCarousel.addEventListener('focusin', () => {
      capabilityFocusPaused = true;
      stopCapabilityCarousel();
    });
    capabilityCarousel.addEventListener('focusout', (event) => {
      if (event.relatedTarget && capabilityCarousel.contains(event.relatedTarget)) return;
      capabilityFocusPaused = false;
      startCapabilityCarousel();
    });
    document.addEventListener('visibilitychange', startCapabilityCarousel);

    renderCapabilityCarousel();
    startCapabilityCarousel();
  }

  root.querySelectorAll('.marquee-track').forEach((track) => {
    const group = track.querySelector('.marquee-group');
    if (!group || track.children.length > 1) return;
    const clone = group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    track.appendChild(clone);
  });

  const videoCards = [...root.querySelectorAll('[data-video-card]')];
  const hoverPlayback = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const pauseVideoCard = (card, reset = true) => {
    const video = card?.querySelector('video');
    if (!video) return;
    video.pause();
    if (reset) {
      try { video.currentTime = 0; } catch (_) {}
    }
    card.classList.remove('is-active', 'is-playing');
    card.setAttribute('aria-pressed', 'false');
  };

  const playVideoCard = (card) => {
    const video = card?.querySelector('video');
    if (!video) return;
    videoCards.forEach((item) => { if (item !== card) pauseVideoCard(item); });
    card.classList.add('is-active');
    card.setAttribute('aria-pressed', 'true');
    video.muted = true;
    const playRequest = video.play();
    if (playRequest && typeof playRequest.then === 'function') {
      playRequest.then(() => card.classList.add('is-playing')).catch(() => {
        card.classList.remove('is-playing');
        card.setAttribute('aria-pressed', 'false');
      });
    } else {
      card.classList.add('is-playing');
    }
  };

  videoCards.forEach((card) => {
    const togglePlayback = () => {
      if (card.classList.contains('is-playing')) pauseVideoCard(card);
      else playVideoCard(card);
    };

    if (hoverPlayback) {
      card.addEventListener('pointerenter', () => playVideoCard(card));
      card.addEventListener('pointerleave', () => pauseVideoCard(card));
      card.addEventListener('focusin', () => playVideoCard(card));
      card.addEventListener('focusout', () => pauseVideoCard(card));
    } else {
      card.addEventListener('click', togglePlayback);
    }

    card.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      togglePlayback();
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) videoCards.forEach((card) => pauseVideoCard(card));
  });

  const cards = [...root.querySelectorAll('.cooperation-card')];
  const detailTitle = root.querySelector('.cooperation-detail__copy strong');
  const detailText = root.querySelector('.cooperation-detail__copy p');
  const detailCopy = root.querySelector('.cooperation-detail__copy');
  const cooperationCopy = {
    stock: ['Product supply planned around your order and destination.', 'Confirm availability, order minimum, configuration, packing and delivery timing in a written quotation.'],
    custom: ['A product configuration made specifically for your project.', 'Review design, logo, color, size and gameplay options, with feasibility and timing confirmed in your quotation.'],
    licensing: ['A cooperation model reviewed for your market and product mix.', 'Discuss territory, buyer profile, order expectations and the sales materials available for qualified partners.']
  };
  cards.forEach((card) => card.addEventListener('click', () => {
    cards.forEach((item) => item.classList.toggle('is-active', item === card));
    const copy = cooperationCopy[card.dataset.cooperationMode];
    if (copy && detailTitle && detailText) {
      detailCopy?.classList.add('is-changing');
      window.setTimeout(() => {
        detailTitle.textContent = copy[0];
        detailText.textContent = copy[1];
        detailCopy?.classList.remove('is-changing');
      }, reduceMotion ? 0 : 170);
    }
  }));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeQuote(); closeMenu(); }
  });

  const params = new URLSearchParams(window.location.search);
  if (params.get('contact_posted') === 'true') showToast('Thank you — your inquiry has been sent.');
};

const initAllShunyangHomes = (scope = document) => {
  scope.querySelectorAll('.shunyang-home').forEach(initShunyangHome);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllShunyangHomes());
} else {
  initAllShunyangHomes();
}

document.addEventListener('shopify:section:load', (event) => initAllShunyangHomes(event.target));
})();
